/**
 * This component will show the booking info and calculated total price.
 * I.e. dates and other details related to payment decision in receipt format.
 */
import classNames from 'classnames';
import { bool, func, oneOf, string } from 'prop-types';
import React from 'react';

import { injectIntl, intlShape } from '../../util/reactIntl';
import {
  LINE_ITEM_CUSTOMER_COMMISSION,
  LINE_ITEM_HOUR,
  LINE_ITEM_PROVIDER_COMMISSION,
  LISTING_UNIT_TYPES,
  propTypes,
} from '../../util/types';

import LineItemBookingPeriod from './LineItemBookingPeriod';
import LineItemCustomerCommissionMaybe from './LineItemCustomerCommissionMaybe';
import LineItemCustomerCommissionRefundMaybe from './LineItemCustomerCommissionRefundMaybe';
import LineItemPickupFeeMaybe from './LineItemPickupFeeMaybe';
import LineItemProviderCommissionMaybe from './LineItemProviderCommissionMaybe';
import LineItemProviderCommissionRefundMaybe from './LineItemProviderCommissionRefundMaybe';
import LineItemRefundMaybe from './LineItemRefundMaybe';
import LineItemShippingFeeMaybe from './LineItemShippingFeeMaybe';
import LineItemSubTotalMaybe from './LineItemSubTotalMaybe';
import LineItemTotalPrice from './LineItemTotalPrice';
import LineItemUnknownItemsMaybe from './LineItemUnknownItemsMaybe';

import useTimezone from '../../hooks/useTimezone';
import { isProcessWithoutPayment } from '../../transactions/transaction';
import {
  isDigitalProductListingType,
  isIndividualSession,
} from '../../util/soulwise-marketplace/listingType';
import TimezoneSelector from '../TimezoneSelector';
import CustomerDiscountForm from './CustomerDiscountForm';
import LineItemCustomerDiscountMaybe from './LineItemCustomerDiscountMaybe';
import LineItemProviderGlobalDiscountNote from './LineItemProviderGlobalDiscountNote';
import LineItemPriceTierMaybe from './LineItemPriceTierMaybe';
import { ListingTotalPrice } from './ListingTotalPrice';
import css from './OrderBreakdown.module.css';

export const OrderBreakdownComponent = props => {
  const {
    rootClassName,
    className,
    userRole,
    transaction,
    booking,
    listing,
    intl,
    dateType,
    timeZone,
    currency,
    marketplaceName,
    showTimezoneSelector,
    hidePrice,
    totalOnly,
    // Discount validation props
    hasDiscountCodeForm,
    validateDiscountInProgress,
    validateDiscountError,
    onValidateDiscount,
  } = props;

  const [userTimeZone, setUserTimeZone] = useTimezone();
  const isIndividualListing = isIndividualSession(listing?.attributes?.publicData?.listingType);
  // Use userTimeZone only if showTimezoneSelector is true and userTimeZone is set, otherwise use timeZone prop
  const effectiveTimeZone = showTimezoneSelector && userTimeZone ? userTimeZone : timeZone;

  const isCustomer = userRole === 'customer';
  const isProvider = userRole === 'provider';
  const allLineItems = transaction.attributes.lineItems || [];
  // We'll show only line-items that are specific for the current userRole (customer vs provider)
  const lineItems = allLineItems.filter(lineItem => lineItem.includeFor.includes(userRole));
  const unitLineItem = lineItems.find(
    item => LISTING_UNIT_TYPES.includes(item.code) && !item.reversal
  );
  // Line-item code that matches with base unit: day, night, hour, item
  const lineItemUnitType = unitLineItem?.code || LINE_ITEM_HOUR;

  const hasCommissionLineItem = lineItems.find(item => {
    const hasCustomerCommission = isCustomer && item.code === LINE_ITEM_CUSTOMER_COMMISSION;
    const hasProviderCommission = isProvider && item.code === LINE_ITEM_PROVIDER_COMMISSION;
    return (hasCustomerCommission || hasProviderCommission) && !item.reversal;
  });

  const hasNoPayment = isProcessWithoutPayment(transaction?.attributes?.processName);
  const isDigitalListing = isDigitalProductListingType(
    listing?.attributes?.publicData?.listingType
  );
  const hideNoPaymentTotalForDigital = hasNoPayment && isDigitalListing;

  const classes = classNames(rootClassName || css.root, className);

  const handleDiscountSubmit = userTryOnDiscountCode => {
    if (onValidateDiscount && listing?.id) {
      onValidateDiscount(listing.id, userTryOnDiscountCode);
    }
  };

  /**
   * OrderBreakdown contains different line items:
   *
   * LineItemBookingPeriod: prints booking start and booking end types. Prop dateType
   * determines if the date and time or only the date is shown
   *
   * LineItemShippingFeeMaybe: prints the shipping fee (combining additional fee of
   * additional items into it).
   *
   * LineItemShippingFeeRefundMaybe: prints the amount of refunded shipping fee
   *
   * LineItemBasePriceMaybe: prints the base price calculation for the listing, e.g.
   * "$150.00 * 2 nights $300"
   *
   * LineItemUnknownItemsMaybe: prints the line items that are unknown. In ideal case there
   * should not be unknown line items. If you are using custom pricing, you should create
   * new custom line items if you need them.
   *
   * LineItemSubTotalMaybe: prints subtotal of line items before possible
   * commission or refunds
   *
   * LineItemRefundMaybe: prints the amount of refund
   *
   * LineItemCustomerCommissionMaybe: prints the amount of customer commission
   * The default transaction process used by this template doesn't include the customer commission.
   *
   * LineItemCustomerCommissionRefundMaybe: prints the amount of refunded customer commission
   *
   * LineItemProviderCommissionMaybe: prints the amount of provider commission
   *
   * LineItemProviderCommissionRefundMaybe: prints the amount of refunded provider commission
   *
   * LineItemTotalPrice: prints total price of the transaction
   *
   */

  const breakdownWithoutPayment = (
    <div className={classes}>
      <LineItemBookingPeriod
        booking={booking}
        code={lineItemUnitType}
        dateType={dateType}
        timeZone={effectiveTimeZone}
      />

      {showTimezoneSelector && (
        <TimezoneSelector
          currentTimezone={userTimeZone}
          onChange={setUserTimeZone}
          intl={intl}
          rootClassName={css.timezoneSelector}
        />
      )}
      {!hideNoPaymentTotalForDigital ? (
        <>
          <hr className={css.totalDivider} />
          <ListingTotalPrice listing={listing} intl={intl} />
        </>
      ) : null}
    </div>
  );

  const breakdownWithPayment = (
    <div className={classes}>
      <LineItemBookingPeriod
        booking={booking}
        code={lineItemUnitType}
        dateType={dateType}
        timeZone={effectiveTimeZone}
      />
      <LineItemPriceTierMaybe transaction={transaction} intl={intl} />

      {showTimezoneSelector && (
        <TimezoneSelector
          currentTimezone={userTimeZone}
          onChange={setUserTimeZone}
          intl={intl}
          rootClassName={css.timezoneSelector}
        />
      )}

      {!hidePrice && (
        <>
          <hr className={css.totalDivider} />
          {totalOnly ? (
            <LineItemTotalPrice transaction={transaction} isProvider={isProvider} intl={intl} />
          ) : (
            <>
              {hasDiscountCodeForm && (
                <>
                  <CustomerDiscountForm
                    validateDiscountError={validateDiscountError}
                    onSubmit={handleDiscountSubmit}
                    inProgress={validateDiscountInProgress}
                  />
                  <hr className={css.totalDivider} />
                </>
              )}

              <LineItemCustomerDiscountMaybe
                lineItems={lineItems}
                intl={intl}
                code={lineItemUnitType}
                transaction={transaction}
                currency={currency}
              />

              <LineItemShippingFeeMaybe lineItems={lineItems} intl={intl} />
              <LineItemPickupFeeMaybe lineItems={lineItems} intl={intl} />
              <LineItemUnknownItemsMaybe
                lineItems={lineItems}
                isProvider={isProvider}
                intl={intl}
              />

              <LineItemSubTotalMaybe
                lineItems={lineItems}
                code={lineItemUnitType}
                userRole={userRole}
                intl={intl}
                marketplaceCurrency={currency}
              />
              <LineItemRefundMaybe
                lineItems={lineItems}
                intl={intl}
                marketplaceCurrency={currency}
              />

              <LineItemCustomerCommissionMaybe
                lineItems={lineItems}
                isCustomer={isCustomer}
                marketplaceName={marketplaceName}
                intl={intl}
              />
              <LineItemCustomerCommissionRefundMaybe
                lineItems={lineItems}
                isCustomer={isCustomer}
                marketplaceName={marketplaceName}
                intl={intl}
              />

              <LineItemProviderCommissionMaybe
                lineItems={lineItems}
                isProvider={isProvider}
                marketplaceName={marketplaceName}
                intl={intl}
              />
              <LineItemProviderCommissionRefundMaybe
                lineItems={lineItems}
                isProvider={isProvider}
                marketplaceName={marketplaceName}
                intl={intl}
              />

              <LineItemTotalPrice transaction={transaction} isProvider={isProvider} intl={intl} />

              <LineItemProviderGlobalDiscountNote
                lineItems={lineItems}
                transaction={transaction}
                isProvider={isProvider}
                currency={currency}
                intl={intl}
              />
            </>
          )}
        </>
      )}

      {/* {hasCommissionLineItem ? (
        <span className={css.feeInfo}>
          <FormattedMessage id="OrderBreakdown.commissionFeeNote" />
        </span>
      ) : null} */}
    </div>
  );

  return hasNoPayment ? breakdownWithoutPayment : breakdownWithPayment;
};

OrderBreakdownComponent.defaultProps = {
  rootClassName: null,
  className: null,
  booking: null,
  dateType: null,
};

OrderBreakdownComponent.propTypes = {
  rootClassName: string,
  className: string,

  marketplaceName: string.isRequired,
  userRole: oneOf(['customer', 'provider']).isRequired,
  transaction: propTypes.transaction.isRequired,
  booking: propTypes.booking,
  dateType: propTypes.dateType,
  showTimezoneSelector: bool,
  hidePrice: bool,
  totalOnly: bool,

  // Discount validation props
  validateDiscountInProgress: bool,
  validateDiscountError: string,
  onValidateDiscount: func,

  // from injectIntl
  intl: intlShape.isRequired,
};

const OrderBreakdown = injectIntl(OrderBreakdownComponent);

OrderBreakdown.displayName = 'OrderBreakdown';

export default OrderBreakdown;
